import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { ALARM_SOUNDS, playAlarm } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Save, Play, Volume2, ArrowLeft, Layers, X, History } from "lucide-react";
import SupersetEditor from "../components/SupersetEditor";
import { useAuth } from "../context/AuthContext";
import { distanceLabel, metresToDisplay, displayToMetres } from "../lib/distance";

// Build a unique block id for a superset block within a workout exercises[] array.
const makeBlockId = () => `blk_${Math.random().toString(36).slice(2, 10)}`;

// Default exercise-item factory — same template used by both manual add and the
// auto-mirror code path.
function buildExerciseItem(ex, restSeconds) {
  const tracksDistance = ex.tracking_unit === "distance";
  return {
    exercise_id: ex.exercise_id,
    name: ex.name,
    muscle_groups: ex.muscle_groups,
    tracking_unit: ex.tracking_unit || "reps",
    sets: tracksDistance
      ? [{ reps: 0, weight: 0, distance_m: 10, completed: false }, { reps: 0, weight: 0, distance_m: 10, completed: false }, { reps: 0, weight: 0, distance_m: 10, completed: false }]
      : [{ reps: 10, weight: 0, completed: false }, { reps: 10, weight: 0, completed: false }, { reps: 10, weight: 0, completed: false }],
    rest_seconds: restSeconds,
    notes: "",
    block_type: "single",
  };
}

// If the exercise name contains "(Left)" or "(Right)", return the mirrored name
// the workout builder should auto-add. Case-insensitive match keeps it forgiving.
function mirrorExerciseName(name) {
  if (!name) return null;
  const reLeft = /\(\s*Left\s*\)/i;
  const reRight = /\(\s*Right\s*\)/i;
  if (reLeft.test(name)) return name.replace(reLeft, "(Right)");
  if (reRight.test(name)) return name.replace(reRight, "(Left)");
  return null;
}

export default function WorkoutBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = !!id;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("Body Building");
  const [exercises, setExercises] = useState([]);
  const [restSeconds, setRestSeconds] = useState(60);
  const [alarmSound, setAlarmSound] = useState("beep");
  const [library, setLibrary] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [search, setSearch] = useState("");
  const [supersets, setSupersets] = useState([]);
  const [showSSPicker, setShowSSPicker] = useState(false);
  const [showSSEditor, setShowSSEditor] = useState(false);
  const { user } = useAuth();
  const distUnit = user?.distance_unit || "km";

  useEffect(() => {
    (async () => {
      const [lib, ss] = await Promise.all([api.get("/exercises"), api.get("/supersets")]);
      setLibrary(lib.data);
      setSupersets(ss.data);
      if (isEdit) {
        const { data } = await api.get(`/workouts/${id}`);
        setName(data.name); setDescription(data.description || ""); setCategory(data.category || "Body Building");
        setExercises(data.exercises || []); setRestSeconds(data.rest_seconds || 60); setAlarmSound(data.alarm_sound || "beep");
      }
    })();
  }, [id, isEdit]);

  const addExercise = (ex) => {
    const newItems = [buildExerciseItem(ex, restSeconds)];

    // Auto-mirror: when adding a "(Left)" or "(Right)" variant, also add its
    // mirrored sibling so the user trains both sides equally. Detection is purely
    // by name pattern so any future mirrored pair (Single-Arm Row Left/Right, etc.)
    // works automatically without backend changes.
    const mirrorName = mirrorExerciseName(ex.name);
    if (mirrorName) {
      const sibling = library.find((x) => x.name === mirrorName);
      if (sibling) {
        newItems.push(buildExerciseItem(sibling, restSeconds));
        toast.success(`Mirrored: also added ${sibling.name}`);
      }
    }
    setExercises((prev) => [...prev, ...newItems]);
    setShowAddDialog(false);
  };

  const insertSuperset = (ss) => {
    const blockId = makeBlockId();
    const ssRest = ss.rest_seconds ?? restSeconds;
    const items = (ss.exercises || []).map((e, i) => ({
      exercise_id: e.exercise_id,
      name: e.name,
      muscle_groups: e.muscle_groups || [],
      sets: (e.sets || [{ reps: 10, weight: 0 }]).map((s) => ({ reps: s.reps, weight: s.weight, completed: false })),
      // Only the LAST exercise in a superset round triggers the rest timer; others have rest_seconds=0
      rest_seconds: i === (ss.exercises.length - 1) ? ssRest : 0,
      notes: "",
      block_id: blockId,
      block_type: "superset",
      superset_name: ss.name,
      superset_id: ss.superset_id || null,
    }));
    setExercises((prev) => [...prev, ...items]);
    setShowSSPicker(false);
    toast.success(`Added superset: ${ss.name}`);
  };

  const unwrapSuperset = (blockId) => {
    if (!window.confirm("Break this superset apart? Exercises will remain but no longer be grouped.")) return;
    setExercises((prev) => prev.map((e) => e.block_id === blockId
      ? { ...e, block_id: null, block_type: "single", superset_name: null, superset_id: null }
      : e));
  };

  const removeBlock = (blockId) => {
    setExercises((prev) => prev.filter((e) => e.block_id !== blockId));
  };

  const removeEx = (idx) => setExercises((p) => p.filter((_, i) => i !== idx));
  const updateSets = (idx, sets) => setExercises((p) => p.map((e, i) => i === idx ? { ...e, sets } : e));
  const updateRest = (idx, val) => setExercises((p) => p.map((e, i) => i === idx ? { ...e, rest_seconds: val } : e));
  const addSet = (idx) => {
    const ex = exercises[idx];
    const last = ex.sets[ex.sets.length - 1] || {};
    const newSet = ex.tracking_unit === "distance"
      ? { reps: 0, weight: last.weight ?? 0, distance_m: last.distance_m ?? 10, completed: false }
      : { reps: last.reps ?? 10, weight: last.weight ?? 0, completed: false };
    updateSets(idx, [...ex.sets, newSet]);
  };
  const removeSet = (idx, si) => updateSets(idx, exercises[idx].sets.filter((_, k) => k !== si));
  const setSetField = (idx, si, field, val) => {
    const next = exercises[idx].sets.map((s, k) => k === si ? { ...s, [field]: val } : s);
    updateSets(idx, next);
  };

  const save = async () => {
    if (!name) { toast.error("Give your workout a name"); return; }
    if (exercises.length === 0) { toast.error("Add at least one exercise"); return; }
    const body = { name, description, category, exercises, rest_seconds: restSeconds, alarm_sound: alarmSound };
    try {
      const res = isEdit ? await api.put(`/workouts/${id}`, body) : await api.post("/workouts", body);
      toast.success("Saved");
      nav(`/workouts/${res.data.workout_id || id}`);
      if (!isEdit) nav("/library");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const filteredLib = library.filter((x) => !search || x.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <button onClick={() => nav(-1)} className="flex items-center text-neutral-400 hover:text-blue-500 text-xs uppercase tracking-widest mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mb-5">{isEdit ? "Edit Workout" : "New Workout"}</h1>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-xs tracking-widest uppercase text-neutral-400">Workout Name</Label>
          <Input data-testid="workout-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Push Day" className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
        </div>
        <div>
          <Label className="text-xs tracking-widest uppercase text-neutral-400">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="workout-category-select" className="mt-2 bg-neutral-900 border-neutral-800 rounded-none h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-neutral-900 border-neutral-800">
              {["Body Building","Strength Building","CrossFit","HIIT","Yoga","Pilates","Cardio"].map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-4">
        <Label className="text-xs tracking-widest uppercase text-neutral-400">Description</Label>
        <Textarea data-testid="workout-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div>
          <Label className="text-xs tracking-widest uppercase text-neutral-400">Default Rest (seconds)</Label>
          <Input data-testid="rest-seconds-input" type="number" min={15} value={restSeconds} onChange={(e) => setRestSeconds(Number(e.target.value))} className="mt-2 bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
        </div>
        <div>
          <Label className="text-xs tracking-widest uppercase text-neutral-400">Alarm Sound</Label>
          <div className="flex gap-2 mt-2">
            <Select value={alarmSound} onValueChange={setAlarmSound}>
              <SelectTrigger data-testid="alarm-select" className="bg-neutral-900 border-neutral-800 rounded-none h-11 flex-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-neutral-900 border-neutral-800">
                {ALARM_SOUNDS.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="button" onClick={() => playAlarm(alarmSound)} variant="outline" data-testid="test-alarm-btn" className="bg-transparent border-neutral-700 text-white rounded-none h-11">
              <Volume2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h2 className="font-display text-2xl uppercase">Exercises ({exercises.length})</h2>
        <div className="flex gap-2">
          <Button data-testid="add-superset-btn" variant="outline" onClick={() => setShowSSPicker(true)} className="bg-transparent border-blue-500/60 text-white hover:border-blue-500 hover:bg-blue-500/10 rounded-none uppercase tracking-wider font-display h-9">
            <Layers className="w-4 h-4 mr-1 text-blue-500" /> Superset
          </Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-exercise-btn" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-9">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-xl max-h-[80vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Add Exercise</DialogTitle></DialogHeader>
              <Input data-testid="add-exercise-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="bg-neutral-900 border-neutral-800 rounded-none text-white" />
              <div className="divide-y divide-neutral-800 border border-neutral-800 max-h-96 overflow-y-auto">
                {filteredLib.map((ex) => (
                  <button key={ex.exercise_id} data-testid={`lib-ex-${ex.exercise_id}`} onClick={() => addExercise(ex)} className="w-full flex items-center justify-between p-3 hover:bg-neutral-900 text-left">
                    <div>
                      <div className="font-semibold">{ex.name}</div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider">{ex.category}</div>
                    </div>
                    <Plus className="w-4 h-4 text-blue-500" />
                  </button>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-4">
        {renderExerciseBlocks(exercises, {
          onRemoveEx: removeEx,
          onRemoveBlock: removeBlock,
          onUnwrapBlock: unwrapSuperset,
          onAddSet: addSet,
          onRemoveSet: removeSet,
          onSetField: setSetField,
          onUpdateRest: updateRest,
          distUnit,
        })}
      </div>

      <div className="flex gap-3 mt-6">
        <Button data-testid="save-workout-btn" onClick={save} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11 flex-1">
          <Save className="w-4 h-4 mr-2" /> Save
        </Button>
        {isEdit && (
          <Button data-testid="start-workout-btn" onClick={() => nav(`/active/${id}`)} variant="outline" className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-11">
            <Play className="w-4 h-4 mr-2" /> Start
          </Button>
        )}
        {isEdit && (
          <Button
            data-testid="history-workout-btn"
            onClick={() => nav(`/workouts/${id}/history`)}
            variant="outline"
            title="View previous results"
            className="bg-transparent border-neutral-700 text-white hover:bg-emerald-500/10 hover:border-emerald-500 hover:text-emerald-400 rounded-none uppercase tracking-wider font-display h-11"
          >
            <History className="w-4 h-4 mr-2" /> History
          </Button>
        )}
      </div>

      {/* Superset picker — pick saved or build new */}
      <Dialog open={showSSPicker} onOpenChange={setShowSSPicker}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">Add Superset</DialogTitle>
            <DialogDescription className="text-xs text-neutral-400">
              Pick a saved superset, or build a new one. All exercises in the superset get added back-to-back with no rest between.
            </DialogDescription>
          </DialogHeader>
          {supersets.length > 0 ? (
            <div className="divide-y divide-neutral-800 border border-neutral-800 max-h-72 overflow-y-auto">
              {supersets.map((ss) => (
                <button key={ss.superset_id} data-testid={`pick-ss-${ss.superset_id}`} onClick={() => insertSuperset(ss)} className="w-full flex items-center gap-3 p-3 hover:bg-neutral-900 text-left">
                  <div className="w-9 h-9 border border-blue-500/40 flex items-center justify-center bg-blue-500/5 shrink-0">
                    <Layers className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base uppercase truncate">{ss.name}</div>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-wider truncate">{ss.exercises.length} exercises · {ss.exercises.map((e) => e.name).join(" → ")}</div>
                  </div>
                  <Plus className="w-4 h-4 text-blue-500 shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            <div className="border border-dashed border-neutral-800 p-4 text-center text-xs text-neutral-500">No saved supersets yet.</div>
          )}
          <Button
            data-testid="build-new-ss-btn"
            onClick={() => { setShowSSPicker(false); setShowSSEditor(true); }}
            className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10 mt-3"
          >
            <Plus className="w-4 h-4 mr-1" /> Build new superset
          </Button>
        </DialogContent>
      </Dialog>

      <SupersetEditor
        open={showSSEditor}
        onOpenChange={setShowSSEditor}
        existing={null}
        onSaved={(saved) => {
          setSupersets((p) => [saved, ...p.filter((x) => x.superset_id !== saved.superset_id)]);
          insertSuperset(saved);
        }}
      />
    </div>
  );
}

// Group consecutive items with the same block_id (and block_type=='superset') into one card.
function renderExerciseBlocks(items, h) {
  const out = [];
  let i = 0;
  while (i < items.length) {
    const ex = items[i];
    if (ex.block_type === "superset" && ex.block_id) {
      const blockId = ex.block_id;
      const group = [];
      let j = i;
      while (j < items.length && items[j].block_id === blockId) {
        group.push({ ex: items[j], idx: j });
        j++;
      }
      out.push(<SupersetBlock key={`blk-${blockId}`} group={group} blockId={blockId} h={h} />);
      i = j;
    } else {
      out.push(<SingleExerciseCard key={`ex-${i}`} ex={ex} idx={i} h={h} />);
      i++;
    }
  }
  return out;
}

function SingleExerciseCard({ ex, idx, h }) {
  return (
    <div data-testid={`wk-ex-${idx}`} className="border border-neutral-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-display text-xl uppercase">{ex.name}</div>
          <div className="text-xs text-neutral-500 uppercase tracking-wider">{(ex.muscle_groups || []).join(", ")}</div>
        </div>
        <Button data-testid={`remove-ex-${idx}`} size="icon" variant="ghost" onClick={() => h.onRemoveEx(idx)} className="text-neutral-400 hover:text-blue-500">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      <SetGrid ex={ex} idx={idx} h={h} />
      <div className="flex items-center gap-3 mt-3">
        <Button data-testid={`add-set-${idx}`} size="sm" variant="outline" onClick={() => h.onAddSet(idx)} className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider">
          <Plus className="w-3 h-3 mr-1" /> Set
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Label className="text-xs uppercase tracking-widest text-neutral-400">Rest</Label>
          <Input data-testid={`rest-${idx}`} type="number" value={ex.rest_seconds} onChange={(e) => h.onUpdateRest(idx, Number(e.target.value))} className="w-20 bg-neutral-900 border-neutral-800 rounded-none h-8 text-white" />
          <span className="text-xs text-neutral-500">sec</span>
        </div>
      </div>
    </div>
  );
}

function SupersetBlock({ group, blockId, h }) {
  const name = group[0].ex.superset_name || "Superset";
  return (
    <div data-testid={`wk-superset-${blockId}`} className="border border-blue-500/60 bg-blue-500/5 p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Layers className="w-4 h-4 text-blue-500 shrink-0" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-blue-500 font-bold shrink-0">Superset</div>
          <div className="font-display text-xl uppercase truncate">{name}</div>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button data-testid={`unwrap-ss-${blockId}`} size="icon" variant="ghost" onClick={() => h.onUnwrapBlock(blockId)} className="text-neutral-400 hover:text-blue-500" title="Break apart">
            <X className="w-4 h-4" />
          </Button>
          <Button data-testid={`remove-ss-${blockId}`} size="icon" variant="ghost" onClick={() => h.onRemoveBlock(blockId)} className="text-neutral-400 hover:text-blue-500" title="Remove block">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        {group.map(({ ex, idx }, k) => (
          <div key={idx} data-testid={`wk-ex-${idx}`} className="border border-neutral-800 bg-neutral-950 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="min-w-0">
                <div className="font-display text-base uppercase truncate">{String.fromCharCode(65 + k)}. {ex.name}</div>
                <div className="text-[10px] text-neutral-500 uppercase tracking-wider truncate">{(ex.muscle_groups || []).join(", ")}</div>
              </div>
              <Button data-testid={`remove-ex-${idx}`} size="icon" variant="ghost" onClick={() => h.onRemoveEx(idx)} className="text-neutral-400 hover:text-blue-500">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            <SetGrid ex={ex} idx={idx} h={h} compact />
            <div className="flex items-center gap-3 mt-2">
              <Button data-testid={`add-set-${idx}`} size="sm" variant="outline" onClick={() => h.onAddSet(idx)} className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider h-7 text-[11px]">
                <Plus className="w-3 h-3 mr-1" /> Set
              </Button>
              {k === group.length - 1 ? (
                <div className="flex items-center gap-2 ml-auto">
                  <Label className="text-[10px] uppercase tracking-widest text-neutral-400">Rest after round</Label>
                  <Input data-testid={`rest-${idx}`} type="number" value={ex.rest_seconds} onChange={(e) => h.onUpdateRest(idx, Number(e.target.value))} className="w-16 bg-neutral-900 border-neutral-800 rounded-none h-7 text-white text-xs" />
                  <span className="text-[10px] text-neutral-500">s</span>
                </div>
              ) : (
                <div className="ml-auto text-[10px] uppercase tracking-widest text-neutral-500">→ next exercise (no rest)</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SetGrid({ ex, idx, h, compact = false }) {
  const cell = compact ? "h-8 text-sm" : "h-9";
  const head = compact ? "text-[10px]" : "text-xs";
  const tracksDistance = ex.tracking_unit === "distance";
  const distLabel = distanceLabel(h.distUnit);
  const repsHeader = tracksDistance ? `Distance (${distLabel})` : "Reps";
  return (
    <>
      <div className={`grid grid-cols-[36px_1fr_1fr_36px] gap-2 ${head} tracking-widest uppercase text-neutral-500 mb-1`}>
        <div>Set</div><div>{repsHeader}</div><div>Weight</div><div></div>
      </div>
      {ex.sets.map((s, si) => (
        <div key={si} className="grid grid-cols-[36px_1fr_1fr_36px] gap-2 mb-1 items-center">
          <div className="text-neutral-400 text-xs pl-1">{si + 1}</div>
          {tracksDistance ? (
            <Input
              data-testid={`set-${idx}-${si}-distance`}
              type="number" min={0} step={1}
              value={metresToDisplay(s.distance_m, h.distUnit)}
              onChange={(e) => h.onSetField(idx, si, "distance_m", displayToMetres(e.target.value, h.distUnit))}
              className={`bg-neutral-900 border-neutral-800 rounded-none ${cell} text-white`}
            />
          ) : (
            <Input data-testid={`set-${idx}-${si}-reps`} type="number" min={0} value={s.reps} onChange={(e) => h.onSetField(idx, si, "reps", Number(e.target.value))} className={`bg-neutral-900 border-neutral-800 rounded-none ${cell} text-white`} />
          )}
          <Input data-testid={`set-${idx}-${si}-weight`} type="number" min={0} step={0.5} value={s.weight} onChange={(e) => h.onSetField(idx, si, "weight", Number(e.target.value))} className={`bg-neutral-900 border-neutral-800 rounded-none ${cell} text-white`} />
          <Button size="icon" variant="ghost" onClick={() => h.onRemoveSet(idx, si)} className="text-neutral-500 hover:text-blue-500 h-7 w-7"><Trash2 className="w-3 h-3" /></Button>
        </div>
      ))}
    </>
  );
}
